import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { EmployeeLoginData, getUserLoginData } from '~/data/employee';
import {
  AuthenticatedUserDto,
  EmployeeReadApiClient,
  People,
  fetchFromNewApi,
} from '~/services/Http';

export interface StoreState {
  userDetails?: AuthenticatedUserDto;
  userLoginData?: EmployeeLoginData;
  employeeProfile?: People.EmployeeProfileDto;
  isLoading: boolean;
  failedToLoad: boolean;
  hasData: boolean;
}

const initialState: StoreState = {
  isLoading: false,
  failedToLoad: false,
  hasData: false,
};

export enum AuthActionKeyPrefixes {
  FETCH_AUTHENTICATED_USER_DETAILS = 'auth/FETCH_AUTHENTICATED_USER_DETAILS',
}

export interface AuthenticatedUserDetailsPayload {
  userDetails: AuthenticatedUserDto;
  employeeProfile: People.EmployeeProfileDto;
  userLoginData: EmployeeLoginData;
}

export const fetchAuthenticatedUserDetails = createAsyncThunk<
  AuthenticatedUserDetailsPayload,
  undefined
>(
  AuthActionKeyPrefixes.FETCH_AUTHENTICATED_USER_DETAILS,
  async (): Promise<AuthenticatedUserDetailsPayload> => {
    const userDetailsPromise = (): Promise<AuthenticatedUserDto> =>
      fetchFromNewApi('/iam/auth/test').then((r) => r.json());

    const employeeProfilePromise = () =>
      EmployeeReadApiClient.getPublicProfile('me', undefined).then(
        (r) => r.data,
      );

    return Promise.all([userDetailsPromise(), employeeProfilePromise()]).then(
      async ([
        userDetails,
        employeeProfile,
      ]): Promise<AuthenticatedUserDetailsPayload> => {
        return getUserLoginData(userDetails.username).then(
          (userLoginData): AuthenticatedUserDetailsPayload => {
            return {
              userDetails,
              employeeProfile,
              userLoginData,
            };
          },
        );
      },
    );
  },
);

export const authSlice = createSlice({
  name: '~/components/auth',
  initialState,
  reducers: {},
  extraReducers: {
    [fetchAuthenticatedUserDetails.fulfilled.type]: (
      state: StoreState,
      action: PayloadAction<AuthenticatedUserDetailsPayload>,
    ) => {
      const { userDetails, employeeProfile, userLoginData } = action.payload;

      state.userDetails = userDetails;
      state.userLoginData = userLoginData;
      state.employeeProfile = employeeProfile;
      state.failedToLoad = false;
      state.isLoading = false;
      state.hasData = !!userDetails || !!employeeProfile;
    },
    [fetchAuthenticatedUserDetails.pending.type]: (state: StoreState) => {
      state.isLoading = true;
    },
    [fetchAuthenticatedUserDetails.rejected.type]: (state: StoreState) => {
      state.failedToLoad = true;
    },
  },
});

export const authReducer = authSlice.reducer;
